import type { ContentItemViewNodesType } from '../../../types/contentItemViewNode.type'
import {
    findAncestors,
    getAllChildrenFromHoveredNode,
    getChildElements,
} from '../../../utils/hoveredActionUtils'
import { useWorkspace2Store } from '../../../Workspace2Store'
import { extractTextBeforeColon } from './index'

/**
 * Collects all PT-keyed content and their parents for given PT names
 * @param ptNames - Array of Product Type names
 * @returns Map of content item IDs to their corresponding nodes
 *
 * @example
 * const ptNames = ['PT Name 1', 'PT Name 2']
 * const itemsToActivate = collectPTLinkedContent(ptNames)
 * // Returns Map { 'contentItemId1' => ContentItemViewNodesType, ... }
 */
export const collectPTLinkedContent = (
    ptNames: string[]
): Map<string, Partial<ContentItemViewNodesType>> => {
    const storeState = useWorkspace2Store.getState()
    const latestNodes = storeState.contentItemViewNodes
    const allItemsToActivate = new Map<
        string,
        Partial<ContentItemViewNodesType>
    >()

    ptNames.forEach((ptName) => {
        const isNodeLinkedToPT = (node: any): boolean => {
            const paragraphText = node?.elements?.[0]?.text
            if (!paragraphText?.includes(':')) return false
            const nodePTName = extractTextBeforeColon(paragraphText)
            return nodePTName.toLowerCase() === ptName.toLowerCase()
        }

        // Find all PT-keyed paragraphs for this PT
        const itemsForThisPT = latestNodes.filter(
            (node) =>
                isNodeLinkedToPT(node) &&
                node?.isUserAdded === false &&
                node?.isHidden === true
        )

        // Recursively collects children of a node, re-rooting at each nested PT key
        // so that dtvParentId relationships past nested PT paragraphs are not skipped.
        const collectChildrenDeep = (
            node: Partial<ContentItemViewNodesType>
        ) => {
            const children = getChildElements(node, latestNodes)
            children.forEach((child) => {
                if (!child.id || !child.isHidden) return
                if (allItemsToActivate.has(child.id)) return // already processed
                allItemsToActivate.set(child.id, child)
                // Only recurse into isProductType children — they can have their own
                // subtrees (MFR pills, keyed content). Non-PT children (MFR paragraphs,
                // keyed content) are leaf nodes and never have nested children.
                if (child.isProductType) {
                    collectChildrenDeep(child)
                }
            })
        }

        // Add PT-keyed nodes, their children (at any nesting level), and their parents
        itemsForThisPT.forEach((ptKeyedNode) => {
            if (ptKeyedNode.id) {
                allItemsToActivate.set(ptKeyedNode.id, ptKeyedNode)
            }

            collectChildrenDeep(ptKeyedNode)

            const parents = findAncestors(latestNodes, ptKeyedNode)
            parents.forEach((parent) => {
                if (
                    parent.id &&
                    parent.isHidden &&
                    parent.isUserAdded === false
                ) {
                    allItemsToActivate.set(parent.id, parent)
                }
            })
        })
    })

    return allItemsToActivate
}

/**
 * Collects all children and ancestors of a parent node for activation
 * Uses getAllChildrenFromHoveredNode to handle all edge cases properly
 */
export const collectAllChildrenAndAncestors = (
    parentNode: Partial<ContentItemViewNodesType>
): Partial<ContentItemViewNodesType>[] => {
    const storeState = useWorkspace2Store.getState()
    const latestNodes = storeState.contentItemViewNodes

    // Use getAllChildrenFromHoveredNode to get all affected nodes
    // This handles: children (5 levels deep), parent ancestors, BOD/Manufacturers, etc.
    const updatedNodes = getAllChildrenFromHoveredNode(
        parentNode,
        latestNodes,
        false // false = activate (isHidden = false)
    )

    if (!updatedNodes) return []

    // Extract only the nodes that changed from hidden to visible
    const itemsToActivate: Partial<ContentItemViewNodesType>[] = []
    updatedNodes.forEach((node, index) => {
        const originalNode = latestNodes[index]
        if (originalNode?.isHidden === true && node.isHidden === false) {
            itemsToActivate.push(node)
        }
    })

    return itemsToActivate
}
